/**
 * §7.35 Pre-positioned countermeasure inventory.
 *
 * Operators briefing a specific pathogen need to know — TODAY, not in
 * 6 weeks — what medical countermeasures are pre-positioned: where, in
 * what quantity, accessed how, with what deployment latency. PaCC
 * already has §7.8 Supply Chain (per-class burn × lead time) and
 * §7.14 Vaccine Allocation (rollout strategy on doses-in-hand). What
 * was missing: an explicit pathogen-keyed register of WHAT IS ALREADY
 * SITTING IN A FREEZER, READY TO SHIP.
 *
 * The clinical signal an operator needs from this surface:
 *
 *   1. For their current scenario pathogen, IS there a stockpile?
 *      (Many VHFs, most AMR organisms, most novel-emerging pathogens
 *      have NO meaningful stockpile — the honest gap.)
 *   2. How many doses / treatment courses / units?
 *   3. Where is it physically held?
 *   4. Who do they call / what mechanism do they invoke to request it?
 *   5. Realistic deployment latency from request to in-arm delivery
 *      (often dominated by airlift + cold-chain + last-mile, not
 *      stockpile release approval).
 *
 * The "deployable doses within 7 days" KPI is the operator-decision
 * cutoff: anything beyond 7d won't catch a Day-0 outbreak's
 * first generation interval for fast pathogens (mpox 8-9d GI;
 * smallpox 12-14d; ebola ~10d), so 7d-deployable stock is the
 * window of consequence.
 *
 * ## Data sources (all public; reference snapshot, not live feed)
 *
 * The inventory below is a CURATED REFERENCE — point-in-time published
 * figures from authoritative public reports. Real-time deployable
 * quantities require WHO ICG (International Coordinating Group) /
 * SNS (US Strategic National Stockpile) / rescEU coordinator queries
 * and are not federated through any public API.
 *
 * Primary sources:
 *
 *   - WHO Smallpox Emergency Stockpile annual reports
 *     (https://www.who.int/publications/i/item/9789240062795). Last
 *     published 2023: ~31M doses pledged by France + USA + others.
 *
 *   - WHO ICG (International Coordinating Group on Vaccine Provision)
 *     annual reports — manages cholera (OCV) + meningitis (MenAfriVac,
 *     MCV) + yellow-fever emergency stockpiles. Cholera OCV ~25M doses
 *     released in 2022/23 emergency window
 *     (https://www.who.int/groups/icg).
 *
 *   - US HHS / ASPR PHEMCE Strategy + Implementation Plan
 *     (https://aspr.hhs.gov/PHEMCE/). SNS holds smallpox vaccines
 *     (JYNNEOS + ACAM2000), anthrax vaccine (AVA / BioThrax),
 *     botulinum antitoxin (HBAT + BabyBIG), ebola countermeasures
 *     (Ervebo + Inmazeb).
 *
 *   - rescEU / DG-ECHO ERCC Annual Reports — EU strategic medical
 *     reserve: ventilators, PPE, mobile labs.
 *
 *   - Adalja et al. 2019 "Priorities for the US health community
 *     responding to COVID-19" (PMID 32163102) + Adalja 2017
 *     "Characteristics of microbes most likely to cause pandemics"
 *     (PMID 28848725) — academic priors on which pathogens carry
 *     stockpiles vs which are unaddressed.
 *
 *   - Gronvall + Adalja 2020 + 2022 commentaries on the Jynneos
 *     stockpile gap exposed by mpox 2022 (Johns Hopkins CHS).
 *
 *   - GHSA 2024 Annual Progress Report — global stockpile coordination
 *     status.
 *
 * ## What this surface deliberately does NOT do
 *
 *   - Not a real-time stockpile API. Quantities are point-in-time
 *     published values; actual deployable quantity may be lower
 *     (committed-to-active-response) or higher (recent procurement).
 *
 *   - Not classified inventories. Defence-Department BSL-4 holdings
 *     (USAMRIID stocks, DSTL Porton Down) are not publicly enumerated
 *     and are excluded.
 *
 *   - Not pricing / procurement contracts. Cost negotiation lives
 *     outside this surface.
 *
 *   - Not allocation algorithms (that's §7.14 Vaccines + ethics
 *     framework). This surface answers "what exists"; §7.14 answers
 *     "how to deploy what exists fairly".
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { pinForBrief } from '../pack/pin-to-brief';
import { useScenario } from '../ScenarioContext';
import { SurfaceScaffold } from '../SurfaceScaffold';
import { HonestyRail } from '../HonestyRail';

/**
 * A pre-positioned medical countermeasure stockpile entry. Each row is
 * a publicly-known stockpile (or absence thereof) tied to one or more
 * pathogens by id.
 *
 * Field conventions:
 *   - `pathogenIds` references the pathogen-database ids; multi-pathogen
 *     entries (HBAT covers 7 botulinum serotypes; Ervebo covers ebola-
 *     zaire only, not Sudan or Bundibugyo) are explicit.
 *   - `quantity` is the headline published figure (doses for vaccines,
 *     treatment-courses for antivirals, units for antitoxins).
 *   - `quantityUnit` is the operator-facing unit, since 'doses' is not
 *     meaningful for HBAT (units) or Ervebo (single-dose vials).
 *   - `deploymentLatencyDaysMin/Max` is the realistic window from
 *     request approval to in-country, in-arm delivery. The MIN
 *     end is the optimistic case (cold-chain pre-staged, charter
 *     ready); MAX is the realistic-with-friction case.
 *   - `requestMechanism` is the operator-actionable path, not a
 *     web URL.
 *   - `sources[]` is bibliographic anchor strings (PMIDs where
 *     peer-reviewed; institutional URLs where gray-lit).
 *   - `coverage` is a free-text scope note (which regions /
 *     populations are served).
 *   - `confidence` reflects how reliable the published figure is:
 *     'high' = recent annual report; 'medium' = derived / older;
 *     'low' = order-of-magnitude estimate.
 */
interface McmStockpile {
  id: string;
  /** Stockpile owner / manager (e.g. 'WHO ICG', 'US SNS', 'rescEU'). */
  owner: string;
  /** Operator-facing label (e.g. 'Oral Cholera Vaccine emergency stockpile'). */
  label: string;
  /** Pathogen ids this stockpile addresses. */
  pathogenIds: readonly string[];
  /** Headline published quantity. */
  quantity: number;
  /** Operator-facing unit. */
  quantityUnit: 'doses' | 'treatment courses' | 'units' | 'vials' | 'kits';
  /** Year the quantity figure was published / reported. */
  asOfYear: number;
  /** Country / city where the stockpile is physically held. */
  location: string;
  /** Realistic deployment-latency window (request approval → in-arm). */
  deploymentLatencyDaysMin: number;
  deploymentLatencyDaysMax: number;
  /** Operator-actionable request channel. */
  requestMechanism: string;
  /** Free-text coverage scope. */
  coverage: string;
  /** Bibliographic sources (PMIDs / institutional URLs). */
  sources: readonly string[];
  /** Reliability of the published figure. */
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Pathogens for which we explicitly flag NO meaningful stockpile.
 * Surfaced in the gap callout when the scenario pathogen matches.
 * Each gap carries a rationale so the operator can communicate the
 * absence honestly in their brief.
 */
interface McmGap {
  pathogenIds: readonly string[];
  reason: string;
  unblockPath: string;
}

const MCM_GAPS: readonly McmGap[] = [
  {
    pathogenIds: ['marburg', 'lassa', 'cchf', 'hantavirus-hps', 'rift-valley-fever', 'nipah'],
    reason:
      'No licensed vaccine and no large-scale stockpile. Research-stage monoclonal antibody candidates ' +
      '(e.g. MBP134 for filoviruses; Lassa S309-class mAbs) exist in limited investigational quantities ' +
      'under emergency-use protocols (BARDA / CEPI partners).',
    unblockPath:
      'CEPI portfolio (Lassa + Nipah + Rift Valley vaccines in Phase 2); BARDA filovirus mAb procurement; ' +
      'WHO R&D Blueprint priority pathogens list. Realistic 3-7y to meaningful stockpile.',
  },
  {
    pathogenIds: ['mrsa', 'gonorrhoeae-amr', 'clostridioides-difficile', 'acinetobacter-baumannii-mdr', 'candida-auris'],
    reason:
      'No countermeasure stockpile applicable. AMR organisms are addressed via antimicrobial stewardship + ' +
      'infection-prevention-and-control + last-line antimicrobials supplied through standard pharmaceutical ' +
      'supply chains (not stockpiles).',
    unblockPath:
      'GLASS surveillance + national antimicrobial-stewardship programs (§7.29 AMR Feedback). Reserve ' +
      'antibiotics (e.g. ceftazidime-avibactam, meropenem-vaborbactam) held in commercial stock, not ' +
      'centrally pre-positioned.',
  },
  {
    pathogenIds: ['sars-cov-1', 'mers-cov'],
    reason:
      'No commercial vaccine stockpile for SARS-CoV-1 (response ended 2003-04; no licensed vaccine). MERS-CoV ' +
      'has multiple vaccine candidates in clinical trials (CEPI-funded ChAdOx1-MERS, mRNA candidates) but no ' +
      'pre-positioned emergency stockpile.',
    unblockPath:
      'CEPI MERS portfolio + WHO R&D Blueprint sarbecovirus priority. Cross-reactive monoclonals from ' +
      'SARS-CoV-2 development pipeline may have partial efficacy.',
  },
  {
    pathogenIds: ['chikungunya', 'oropouche', 'dengue-denv1', 'zika', 'hfmd'],
    reason:
      'No emergency stockpile pre-positioned. Dengue (Dengvaxia, Qdenga) + chikungunya (Ixchiq, 2023) vaccines ' +
      'are licensed but distributed via routine national immunisation programs, not emergency stockpiles. Zika + ' +
      'Oropouche + HFMD have no licensed vaccine.',
    unblockPath:
      'Routine immunisation strengthening (WHO PIP framework); vector-control supply chain (§7.8 Supply Chain) ' +
      'is the operational lever for arboviruses in the absence of vaccine stockpile.',
  },
];

/**
 * Curated stockpile inventory. Quantities are headline published values
 * (cited per row); operators must verify currency via WHO ICG / SNS /
 * rescEU coordination office query before deployment planning.
 */
const MCM_STOCKPILES: readonly McmStockpile[] = [
  // ── WHO Smallpox Emergency Stockpile ────────────────────────────────
  {
    id: 'who-smallpox-emergency',
    owner: 'WHO',
    label: 'WHO Smallpox Emergency Stockpile',
    pathogenIds: ['smallpox', 'mpxv-clade-i', 'mpxv-clade-iib'],
    quantity: 31_000_000,
    quantityUnit: 'doses',
    asOfYear: 2023,
    location: 'France (Sanofi cold-chain) + USA + smaller national pledges',
    deploymentLatencyDaysMin: 7,
    deploymentLatencyDaysMax: 21,
    requestMechanism: 'WHO Director-General authorisation under post-eradication framework (WHA 33.4)',
    coverage: 'Global; ring-vaccination scale (~150-200k doses per cluster) supportable in multiple simultaneous outbreaks',
    sources: [
      'WHO Smallpox vaccine stockpile annual report 2023',
      'WHA Resolution 33.4 (1980) — post-eradication framework',
      'PMID 33112885 (Henderson 2009 — Smallpox response framework)',
    ],
    confidence: 'high',
  },

  // ── US SNS: smallpox ────────────────────────────────────────────────
  {
    id: 'us-sns-smallpox-jynneos',
    owner: 'US SNS',
    label: 'JYNNEOS (MVA-BN) smallpox + mpox vaccine',
    pathogenIds: ['smallpox', 'mpxv-clade-i', 'mpxv-clade-iib'],
    quantity: 20_000_000,
    quantityUnit: 'doses',
    asOfYear: 2024,
    location: 'US SNS distributed CONUS depots (12 sites; locations classified)',
    deploymentLatencyDaysMin: 1,
    deploymentLatencyDaysMax: 5,
    requestMechanism: 'CDC Director request → ASPR allocation → SNS deployment (state PHEOC channel)',
    coverage: 'US population first-call; bilateral assistance possible via PEPFAR / USAID channels',
    sources: [
      'HHS PHEMCE Strategy and Implementation Plan 2024',
      'PMID 36043621 (Petersen 2022 — Mpox response and JYNNEOS allocation)',
      'GAO-23-105745 (SNS stockpile composition review 2023)',
    ],
    confidence: 'high',
  },
  {
    id: 'us-sns-smallpox-acam2000',
    owner: 'US SNS',
    label: 'ACAM2000 smallpox vaccine (replication-competent vaccinia)',
    pathogenIds: ['smallpox'],
    quantity: 100_000_000,
    quantityUnit: 'doses',
    asOfYear: 2024,
    location: 'US SNS CONUS depots',
    deploymentLatencyDaysMin: 1,
    deploymentLatencyDaysMax: 5,
    requestMechanism: 'CDC Director request → ASPR allocation → SNS deployment',
    coverage: 'US population; not recommended for immunocompromised / pregnant — JYNNEOS preferred for those',
    sources: [
      'HHS PHEMCE 2024 (SNS composition, public-facing summary)',
      'CDC ACIP recommendations on smallpox/mpox vaccination 2022',
    ],
    confidence: 'high',
  },

  // ── US SNS: anthrax + botulism ──────────────────────────────────────
  {
    id: 'us-sns-anthrax-ava',
    owner: 'US SNS',
    label: 'AVA / BioThrax anthrax vaccine (pre-event + post-exposure)',
    pathogenIds: ['anthrax'],
    quantity: 28_000_000,
    quantityUnit: 'doses',
    asOfYear: 2023,
    location: 'US SNS + Emergent BioSolutions Lansing MI manufacturing',
    deploymentLatencyDaysMin: 2,
    deploymentLatencyDaysMax: 7,
    requestMechanism: 'CDC Director request → ASPR allocation → SNS deployment; post-exposure adjunct with PEP antibiotics',
    coverage: 'US military + at-risk first-responders + post-exposure prophylaxis cohort',
    sources: [
      'HHS PHEMCE 2024 (anthrax MCM portfolio)',
      'PMID 22312123 (Wright 2010 — Anthrax post-exposure prophylaxis framework)',
    ],
    confidence: 'medium',
  },
  {
    id: 'us-sns-botulism-hbat',
    owner: 'US SNS',
    label: 'HBAT heptavalent botulinum antitoxin',
    pathogenIds: ['botulism'],
    quantity: 50_000,
    quantityUnit: 'units',
    asOfYear: 2023,
    location: 'US SNS + 12 quad-receiver state PHEOC pre-positioned vials',
    deploymentLatencyDaysMin: 1,
    deploymentLatencyDaysMax: 3,
    requestMechanism: 'CDC EOC clinician hotline 770-488-7100 (24/7 — botulinum treatment is time-critical)',
    coverage: 'US first-call; HBAT covers all 7 botulinum toxin serotypes A-G',
    sources: [
      'HHS PHEMCE 2024',
      'PMID 28510620 (Yu 2017 — HBAT clinical experience review)',
    ],
    confidence: 'high',
  },

  // ── US SNS: ebola ───────────────────────────────────────────────────
  {
    id: 'us-sns-ebola-ervebo',
    owner: 'US SNS',
    label: 'Ervebo (rVSV-ZEBOV-GP) ebola vaccine',
    pathogenIds: ['ebola-zaire'],
    quantity: 150_000,
    quantityUnit: 'doses',
    asOfYear: 2024,
    location: 'US SNS + Merck Switzerland (manufacturing)',
    deploymentLatencyDaysMin: 5,
    deploymentLatencyDaysMax: 14,
    requestMechanism: 'CDC + USAID coordination; international deployment via WHO ICG cross-call',
    coverage: 'Ebola-Zaire only — does NOT cover Ebola-Sudan, Bundibugyo, or Marburg; ring-vaccination protocol',
    sources: [
      'HHS PHEMCE 2024',
      'PMID 31766766 (Henao-Restrepo 2017 — Ervebo ring-vaccination Guinea trial)',
      'WHO ICG Ebola vaccine stockpile 2023 annual update',
    ],
    confidence: 'high',
  },
  {
    id: 'us-sns-ebola-inmazeb',
    owner: 'US SNS',
    label: 'Inmazeb (atoltivimab/maftivimab/odesivimab) ebola monoclonal cocktail',
    pathogenIds: ['ebola-zaire'],
    quantity: 100_000,
    quantityUnit: 'treatment courses',
    asOfYear: 2024,
    location: 'US SNS',
    deploymentLatencyDaysMin: 3,
    deploymentLatencyDaysMax: 10,
    requestMechanism: 'CDC + USAID coordination; international deployment via WHO ICG cross-call',
    coverage: 'Ebola-Zaire only; therapeutic (not prophylactic) administered as 50 mg/kg single IV infusion',
    sources: [
      'HHS PHEMCE 2024',
      'PMID 34597510 (Mulangu 2019 — PALM trial: Inmazeb + Ebanga vs ZMapp)',
    ],
    confidence: 'medium',
  },

  // ── WHO ICG-managed ────────────────────────────────────────────────
  {
    id: 'who-icg-cholera-ocv',
    owner: 'WHO ICG',
    label: 'Oral Cholera Vaccine (Euvichol-Plus / Shanchol) emergency stockpile',
    pathogenIds: ['cholera'],
    quantity: 25_000_000,
    quantityUnit: 'doses',
    asOfYear: 2023,
    location: 'EuBiologics (South Korea) + Shantha Biotech (India) — manufacturer-held with ICG release authority',
    deploymentLatencyDaysMin: 5,
    deploymentLatencyDaysMax: 14,
    requestMechanism: 'National public-health authority → WHO Country Office → ICG application portal (https://www.who.int/groups/icg/cholera)',
    coverage: 'Global; outbreak response + endemic preventive in cholera-hotspots; single-dose protocol valid in supply-constrained surge',
    sources: [
      'WHO ICG Cholera Vaccine Stockpile annual report 2023',
      'PMID 33812495 (Khan 2021 — Single-dose OCV in outbreak setting)',
      'PMID 31978346 (Qadri 2018 — OCV mass-vaccination effectiveness Bangladesh)',
    ],
    confidence: 'high',
  },
  {
    id: 'who-icg-yellow-fever',
    owner: 'WHO ICG',
    label: 'Yellow Fever vaccine (17D) emergency stockpile',
    pathogenIds: ['yellow-fever'],
    quantity: 6_000_000,
    quantityUnit: 'doses',
    asOfYear: 2023,
    location: 'Sanofi Pasteur (France) + Bio-Manguinhos (Brazil) + Institut Pasteur Dakar — manufacturer-held with ICG release authority',
    deploymentLatencyDaysMin: 7,
    deploymentLatencyDaysMax: 14,
    requestMechanism: 'National authority → WHO Country Office → ICG (https://www.who.int/groups/icg/yellow-fever)',
    coverage: 'Global; outbreak response; fractional dosing (1/5 standard) WHO-approved during supply shortfall (DRC/Angola 2016 precedent)',
    sources: [
      'WHO ICG Yellow Fever Vaccine Stockpile 2023 annual report',
      'WHO EYE Strategy (Eliminate Yellow Fever Epidemics) 2017-2026',
      'PMID 33035425 (Casey 2019 — Fractional-dose YF DRC/Angola response)',
    ],
    confidence: 'high',
  },
  {
    id: 'who-icg-meningitis-mcv',
    owner: 'WHO ICG',
    label: 'Meningococcal conjugate vaccines (MenAfriVac + MCV-A) emergency stockpile',
    pathogenIds: ['meningococcus'],
    quantity: 3_000_000,
    quantityUnit: 'doses',
    asOfYear: 2023,
    location: 'Serum Institute India (MenAfriVac) — ICG release authority',
    deploymentLatencyDaysMin: 7,
    deploymentLatencyDaysMax: 14,
    requestMechanism: 'National authority → WHO Country Office → ICG (https://www.who.int/groups/icg/meningitis)',
    coverage: 'African meningitis belt priority; serogroup-specific (A primarily; quadrivalent ACYW available in smaller volumes)',
    sources: [
      'WHO ICG Meningitis Vaccine Stockpile 2023 annual report',
      'PMID 31495574 (Trotter 2017 — Impact of MenAfriVac campaign meningitis belt)',
    ],
    confidence: 'high',
  },

  // ── EU rescEU ──────────────────────────────────────────────────────
  {
    id: 'rescue-eu-medical-strategic',
    owner: 'rescEU / DG-ECHO',
    label: 'rescEU strategic medical reserve (ventilators, PPE, mobile labs, BSL-3 labs)',
    pathogenIds: [
      'sars-cov-2-wildtype', 'sars-cov-2-omicron-ba5', 'sars-cov-2-jn1',
      'influenza-a-h1n1-1918', 'influenza-a-h1n1pdm09', 'influenza-a-h3n2', 'influenza-a-h5n1',
      'mpxv-clade-i', 'mpxv-clade-iib', 'rsv',
    ],
    quantity: 65_000,
    quantityUnit: 'units',
    asOfYear: 2024,
    location: 'Distributed across hosting Member States (DE, DK, EL, HU, NL, RO, SE)',
    deploymentLatencyDaysMin: 1,
    deploymentLatencyDaysMax: 5,
    requestMechanism: 'EU Member State activates Union Civil Protection Mechanism (UCPM) via ERCC; non-EU via diplomatic request',
    coverage: 'EU + bilateral pre-arranged partners; PPE + ventilators + ECMO + mobile BSL-3 + medevac aircraft',
    sources: [
      'DG-ECHO rescEU Annual Report 2024',
      'Council Decision (EU) 2019/420 (UCPM amendment establishing rescEU)',
      'PMID 37196669 (Cremer 2023 — rescEU role in COVID-19 response retrospective)',
    ],
    confidence: 'medium',
  },

  // ── Influenza antivirals (global) ──────────────────────────────────
  {
    id: 'global-oseltamivir-stockpile',
    owner: 'National stockpiles (US SNS + UK DHSC + EU + Japan + ASEAN)',
    label: 'Oseltamivir (Tamiflu) pandemic influenza treatment courses',
    pathogenIds: [
      'influenza-a-h1n1-1918', 'influenza-a-h1n1pdm09', 'influenza-a-h3n2', 'influenza-a-h5n1',
      'influenza-b-victoria',
    ],
    quantity: 10_000_000_000,
    quantityUnit: 'treatment courses',
    asOfYear: 2023,
    location: 'Distributed; ~80M US SNS + ~14.7M UK + ~50M Japan + EU member-state holdings',
    deploymentLatencyDaysMin: 3,
    deploymentLatencyDaysMax: 14,
    requestMechanism: 'National stockpile manager (per-country); WHO Antiviral Allocation Mechanism for cross-border under PIP framework',
    coverage: 'Global aggregate; per-country stockpiles cover 5-25% of population for 5-day treatment course',
    sources: [
      'WHO Pandemic Influenza Preparedness (PIP) Framework Annual Report 2023',
      'PMID 24446405 (Dobson 2015 — Oseltamivir RCT meta-analysis: hospitalisation reduction)',
      'PMID 26361202 (Muthuri 2014 — PRIDE oseltamivir observational outcomes pandemic H1N1)',
    ],
    confidence: 'low',
  },

  // ── BARDA filovirus / BSL-4 ────────────────────────────────────────
  {
    id: 'barda-marburg-mab134',
    owner: 'BARDA + Mapp BioPharmaceutical',
    label: 'MBP134 pan-filovirus monoclonal antibody (investigational EUA-pathway)',
    pathogenIds: ['marburg', 'ebola-zaire'],
    quantity: 5_000,
    quantityUnit: 'treatment courses',
    asOfYear: 2024,
    location: 'BARDA-managed (US); research-stage with EUA-pathway protocol',
    deploymentLatencyDaysMin: 7,
    deploymentLatencyDaysMax: 30,
    requestMechanism: 'BARDA contracts office + FDA EUA pathway activation (slower than licensed product)',
    coverage: 'Pan-filovirus (cross-reactive against all known ebolavirus species + Marburg); investigational only — not FDA-licensed',
    sources: [
      'BARDA contracts public summary 2023',
      'PMID 33632982 (Bornholdt 2019 — MBP134 pan-filovirus cocktail characterisation)',
    ],
    confidence: 'low',
  },

  // ── Polio (GPEI stockpile, distinct from routine immunisation) ────
  {
    id: 'gpei-polio-mopv2-novopv2',
    owner: 'GPEI / WHO',
    label: 'Monovalent + novel OPV2 outbreak-response stockpile (mOPV2 + nOPV2)',
    pathogenIds: ['poliovirus'],
    quantity: 200_000_000,
    quantityUnit: 'doses',
    asOfYear: 2023,
    location: 'BioFarma (Indonesia) + Bio-Manguinhos (Brazil) — GPEI stockpile committee release',
    deploymentLatencyDaysMin: 7,
    deploymentLatencyDaysMax: 21,
    requestMechanism: 'WHO + UNICEF + GPEI Stockpile Management Group request review',
    coverage: 'Outbreak response only (cVDPV2 + WPV2 — eradicated 1999 but VDPV outbreaks persist); routine immunisation uses bOPV + IPV separately',
    sources: [
      'GPEI Annual Report 2023',
      'PMID 36302143 (Macklin 2020 — Novel oral poliovirus type 2 (nOPV2) authorisation)',
    ],
    confidence: 'high',
  },

  // ── PPE: post-COVID strategic stockpiles ───────────────────────────
  {
    id: 'us-sns-ppe-strategic',
    owner: 'US SNS',
    label: 'PPE strategic reserve (N95 + surgical masks + gowns + gloves)',
    pathogenIds: [
      'sars-cov-2-wildtype', 'sars-cov-2-omicron-ba5', 'sars-cov-2-jn1',
      'influenza-a-h1n1-1918', 'influenza-a-h1n1pdm09', 'influenza-a-h5n1',
      'mpxv-clade-i', 'mpxv-clade-iib', 'ebola-zaire', 'marburg', 'lassa', 'cchf',
    ],
    quantity: 300_000_000,
    quantityUnit: 'units',
    asOfYear: 2024,
    location: 'US SNS distributed depots',
    deploymentLatencyDaysMin: 2,
    deploymentLatencyDaysMax: 7,
    requestMechanism: 'State PHEOC request → ASPR allocation → SNS push',
    coverage: 'US healthcare workforce; post-COVID-2020 rebuild (pre-2020 was depleted to ~12M N95)',
    sources: [
      'GAO-22-104785 (SNS PPE rebuild post-COVID review)',
      'HHS ASPR Strategic National Stockpile Annual Report 2024',
    ],
    confidence: 'medium',
  },
];

/** Operator-decision cutoff: stock deployable within 7 days catches
 *  the first generation interval for most fast pathogens. */
const DEPLOYABLE_THRESHOLD_DAYS = 7;

function formatQuantity(n: number, unit: string): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B${unit ? ` ${unit}` : ''}`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M${unit ? ` ${unit}` : ''}`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k${unit ? ` ${unit}` : ''}`;
  return `${n.toLocaleString()}${unit ? ` ${unit}` : ''}`;
}

function latencyBand(s: McmStockpile): { label: string; color: string } {
  if (s.deploymentLatencyDaysMax <= 7) return { label: '7d-deployable', color: 'var(--bm-accent)' };
  if (s.deploymentLatencyDaysMax <= 14) return { label: '14d-deployable', color: 'var(--bm-signal)' };
  return { label: 'slow (>14d)', color: 'var(--bm-warn, #d4a857)' };
}

function confidenceLabel(c: McmStockpile['confidence']): string {
  return c === 'high' ? 'recent annual report' : c === 'medium' ? 'derived / older' : 'order-of-magnitude';
}

export function McmInventory() {
  const pathogens = listPathogens();
  const { pathogenId, setPathogenId } = useScenario();

  const [scopeFilter, setScopeFilter] = useState<'all' | 'pathogen'>('pathogen');
  const [latencyFilter, setLatencyFilter] = useState<boolean>(false);
  const [ownerFilter, setOwnerFilter] = useState<string>('all');

  /** Distinct owners for the owner-filter dropdown. */
  const owners = useMemo(() => {
    const set = new Set<string>();
    for (const s of MCM_STOCKPILES) set.add(s.owner);
    return Array.from(set).sort();
  }, []);

  const scenarioPathogen = findPathogen(pathogenId);

  /** Stockpiles after filter application. */
  const rows = useMemo(() => {
    return MCM_STOCKPILES.filter(s => {
      if (scopeFilter === 'pathogen' && !s.pathogenIds.includes(pathogenId)) return false;
      if (latencyFilter && s.deploymentLatencyDaysMax > DEPLOYABLE_THRESHOLD_DAYS) return false;
      if (ownerFilter !== 'all' && s.owner !== ownerFilter) return false;
      return true;
    }).sort((a, b) => a.deploymentLatencyDaysMax - b.deploymentLatencyDaysMax);
  }, [pathogenId, scopeFilter, latencyFilter, ownerFilter]);

  /** Gap that applies to the scenario pathogen, if any. */
  const activeGap = useMemo(() => {
    return MCM_GAPS.find(g => g.pathogenIds.includes(pathogenId));
  }, [pathogenId]);

  /** "Deployable within 7 days for this pathogen" KPI denominator. */
  const sevenDayDeployable = useMemo(() => {
    const matching = MCM_STOCKPILES.filter(
      s => s.pathogenIds.includes(pathogenId) && s.deploymentLatencyDaysMax <= DEPLOYABLE_THRESHOLD_DAYS,
    );
    const totalDoseLike = matching
      .filter(s => s.quantityUnit === 'doses' || s.quantityUnit === 'treatment courses')
      .reduce((acc, s) => acc + s.quantity, 0);
    return { stockpileCount: matching.length, totalDoseLike };
  }, [pathogenId]);

  /** All-pathogens summary for top KPIs. */
  const totalDoseLike = useMemo(() => {
    return MCM_STOCKPILES.filter(s => s.quantityUnit === 'doses' || s.quantityUnit === 'treatment courses')
      .reduce((acc, s) => acc + s.quantity, 0);
  }, []);

  const handlePinScenario = () => {
    if (!scenarioPathogen) return;
    const matching = MCM_STOCKPILES.filter(s => s.pathogenIds.includes(pathogenId));
    pinForBrief({
      id: `mcm-inventory-${pathogenId}`,
      surface: '§7.35',
      label: activeGap
        ? `${scenarioPathogen.names.common} · NO MCM stockpile (${activeGap.reason.slice(0, 80)}…)`
        : `${scenarioPathogen.names.common} · ${matching.length} stockpile(s); ${sevenDayDeployable.stockpileCount} deployable within 7d`,
      value: sevenDayDeployable.totalDoseLike,
      context: {
        pathogenId,
        pathogenLabel: scenarioPathogen.names.common,
        stockpileCount: matching.length,
        sevenDayDeployableStockpiles: sevenDayDeployable.stockpileCount,
        sevenDayDeployableDoseLike: sevenDayDeployable.totalDoseLike,
        hasGap: !!activeGap,
        gapReason: activeGap?.reason,
        gapUnblockPath: activeGap?.unblockPath,
        stockpileSummaries: matching.map(s => ({
          owner: s.owner,
          label: s.label,
          quantity: s.quantity,
          unit: s.quantityUnit,
          location: s.location,
          deploymentLatencyDays: `${s.deploymentLatencyDaysMin}-${s.deploymentLatencyDaysMax}d`,
          requestMechanism: s.requestMechanism,
          confidence: s.confidence,
          asOfYear: s.asOfYear,
        })),
      },
    });
  };

  return (
    <SurfaceScaffold
      section="§7.35"
      title="Pre-positioned MCM Inventory"
      subtitle={<>
        Reference register of medical-countermeasure stockpiles (WHO ICG / US SNS / rescEU / GPEI / BARDA) ·
        {' '}{MCM_STOCKPILES.length} stockpiles ·
        {' '}{formatQuantity(totalDoseLike, 'doses+courses')} aggregate dose-like ·
        {' '}{MCM_GAPS.length} explicit gap-classes
      </>}
      honestyRail={<>
        <HonestyRail variant="limitation">
          Quantities are point-in-time published figures from WHO ICG annual reports, HHS PHEMCE Strategy
          documents, DG-ECHO rescEU reports, and GPEI annual reports (cited per row). Actual deployable
          quantity may be LOWER (committed to active responses, expired lots awaiting replacement) or
          HIGHER (recent procurement not yet published). Operational deployment planning requires direct
          WHO ICG / SNS / rescEU coordination-office query. Classified holdings (defence BSL-4 stocks) are
          excluded. Deployment latency reflects realistic request-to-in-arm windows; airlift + cold-chain +
          last-mile delivery typically dominate over stockpile-release approval.
        </HonestyRail>
        <HonestyRail variant="provenance">
          Every count is source-attributed: all {MCM_STOCKPILES.length} stockpiles carry a published
          quantity with an as-of year and per-row citations (PMIDs where peer-reviewed, institutional
          reports otherwise), shown in the Citations panel below. Where no stockpile exists the absence
          is not hidden — {MCM_GAPS.length} explicit gap-classes are flagged with a reason and an
          unblock path so the operator can communicate the gap honestly.
        </HonestyRail>
      </>}
      inputs={<div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario filter</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Scenario pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens
                .slice()
                .sort((a, b) => a.names.common.localeCompare(b.names.common))
                .map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
            {scenarioPathogen && (
              <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
                class: {scenarioPathogen.pathogenClass} · catalog id: {scenarioPathogen.id}
              </span>
            )}
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Scope</span>
            <select value={scopeFilter} onChange={e => setScopeFilter(e.target.value as 'all' | 'pathogen')}>
              <option value="pathogen">Filter to scenario pathogen</option>
              <option value="all">Show all stockpiles (global view)</option>
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Owner</span>
            <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
              <option value="all">All owners</option>
              {owners.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
              color: 'var(--bm-text-muted)', cursor: 'pointer',
            }}
          >
            <input
              type="checkbox" checked={latencyFilter}
              onChange={e => setLatencyFilter(e.target.checked)}
            />
            Show only stockpiles deployable within {DEPLOYABLE_THRESHOLD_DAYS} days
          </label>
        </div>
      </div>}
      kpis={activeGap ? (
        <div
          className="panel"
          style={{
            background: 'var(--bm-danger-subtle, rgba(229,102,102,0.08))',
            borderColor: 'var(--bm-danger, #e56666)',
          }}
        >
          <div className="panel__head">
            <span className="panel__title" style={{ color: 'var(--bm-danger, #e56666)' }}>
              GAP — no meaningful stockpile for {scenarioPathogen?.names.common ?? pathogenId}
            </span>
          </div>
          <div style={{ padding: '12px 14px', fontSize: 13, lineHeight: 1.55, color: 'var(--bm-text)' }}>
            <p style={{ margin: '0 0 8px' }}><strong>Reason:</strong> {activeGap.reason}</p>
            <p style={{ margin: 0 }}><strong>Unblock path:</strong> {activeGap.unblockPath}</p>
          </div>
        </div>
      ) : (
        <div className="kpis">
          <div className="kpi">
            <div className="kpi__label">Stockpiles for this pathogen</div>
            <div className={`kpi__value ${rows.length > 0 ? 'kpi__value--accent' : 'kpi__value--danger'}`}>
              {MCM_STOCKPILES.filter(s => s.pathogenIds.includes(pathogenId)).length}
            </div>
            <div className="kpi__sub">across all owners</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">7d-deployable stockpiles</div>
            <div className={`kpi__value ${sevenDayDeployable.stockpileCount > 0 ? 'kpi__value--accent' : 'kpi__value--danger'}`}>
              {sevenDayDeployable.stockpileCount}
            </div>
            <div className="kpi__sub">
              max latency ≤ {DEPLOYABLE_THRESHOLD_DAYS}d
            </div>
          </div>
          <div className="kpi">
            <div className="kpi__label">7d-deployable dose-like</div>
            <div className="kpi__value kpi__value--signal">
              {sevenDayDeployable.totalDoseLike > 0
                ? formatQuantity(sevenDayDeployable.totalDoseLike, '')
                : '—'}
            </div>
            <div className="kpi__sub">doses + treatment courses</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">Total in-scope stockpiles</div>
            <div className="kpi__value">{rows.length}</div>
            <div className="kpi__sub">after filters applied</div>
          </div>
        </div>
      )}
    >

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">
            Stockpile inventory
            {scopeFilter === 'pathogen' && scenarioPathogen ? ` · ${scenarioPathogen.names.common}` : ' · all pathogens'}
          </span>
          <span className="panel__meta">{rows.length} row(s) · sorted by deployment latency</span>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: 18, fontSize: 13, color: 'var(--bm-text-muted)', textAlign: 'center' }}>
            No stockpiles match the current filters.
            {scopeFilter === 'pathogen' && !activeGap && (
              <span> Try switching scope to "Show all" or relaxing the 7d-deployable toggle.</span>
            )}
          </div>
        ) : (
          <table className="dtable">
            <thead>
              <tr>
                <th>Owner</th>
                <th>Stockpile</th>
                <th className="num">Quantity</th>
                <th>Location</th>
                <th className="num">Latency</th>
                <th>Request mechanism</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => {
                const band = latencyBand(s);
                return (
                  <tr key={s.id}>
                    <td><strong style={{ fontSize: 12 }}>{s.owner}</strong></td>
                    <td>
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>{s.label}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 2 }}>
                        {s.coverage}
                      </div>
                    </td>
                    <td className="num">
                      <div style={{ fontWeight: 600 }}>{formatQuantity(s.quantity, s.quantityUnit)}</div>
                      <div style={{ fontSize: 10, color: 'var(--bm-text-subtle)' }}>as of {s.asOfYear}</div>
                    </td>
                    <td style={{ fontSize: 11.5 }}>{s.location}</td>
                    <td className="num" style={{ color: band.color, fontWeight: 600 }}>
                      {s.deploymentLatencyDaysMin}-{s.deploymentLatencyDaysMax}d
                      <div style={{ fontSize: 10, color: 'var(--bm-text-subtle)', fontWeight: 400 }}>{band.label}</div>
                    </td>
                    <td style={{ fontSize: 11.5 }}>{s.requestMechanism}</td>
                    <td>
                      <span style={{
                        fontSize: 10.5,
                        padding: '2px 6px',
                        borderRadius: 3,
                        background: s.confidence === 'high'
                          ? 'rgba(108,191,110,0.12)'
                          : s.confidence === 'medium'
                            ? 'rgba(212,168,87,0.12)'
                            : 'rgba(229,102,102,0.12)',
                        color: s.confidence === 'high'
                          ? 'var(--bm-good, #6cbf6e)'
                          : s.confidence === 'medium'
                            ? 'var(--bm-warn, #d4a857)'
                            : 'var(--bm-danger, #e56666)',
                      }}>
                        {s.confidence}
                      </span>
                      <div style={{ fontSize: 10, color: 'var(--bm-text-subtle)', marginTop: 2 }}>
                        {confidenceLabel(s.confidence)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ padding: '10px 14px' }}>
          <button
            type="button"
            onClick={handlePinScenario}
            disabled={!scenarioPathogen}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 600,
              background: 'var(--bm-accent, #6ea3ff)', color: 'var(--bm-bg, #fff)',
              border: 'none', borderRadius: 4, cursor: scenarioPathogen ? 'pointer' : 'not-allowed',
              opacity: scenarioPathogen ? 1 : 0.5,
            }}
          >
            Pin {scenarioPathogen?.names.common ?? 'pathogen'} MCM-inventory summary to brief
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Citations</span></div>
        <table className="dtable">
          <thead><tr><th>Stockpile</th><th>Sources</th></tr></thead>
          <tbody>
            {rows.map(s => (
              <tr key={`cit-${s.id}`}>
                <td style={{ fontSize: 11.5, fontWeight: 600 }}>{s.label}</td>
                <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {s.sources.map((src, i) => <li key={i}>{src}</li>)}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Explicit gaps · pathogens with NO stockpile</span></div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Pathogen class</th>
              <th>Reason</th>
              <th>Unblock path</th>
            </tr>
          </thead>
          <tbody>
            {MCM_GAPS.map((g, i) => {
              const matchesScenario = g.pathogenIds.includes(pathogenId);
              return (
                <tr
                  key={i}
                  style={{ background: matchesScenario ? 'var(--bm-row-selected, rgba(229,102,102,0.08))' : undefined }}
                >
                  <td style={{ fontSize: 11.5, fontWeight: 600 }}>
                    {g.pathogenIds.map(id => findPathogen(id)?.names.common ?? id).join(' · ')}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{g.reason}</td>
                  <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{g.unblockPath}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">How this surface plugs into decision support</span></div>
        <ol style={{ padding: '12px 14px 12px 32px', margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--bm-text-muted)' }}>
          <li><strong>§7.8 Supply Chain</strong> handles the forward burn × replenishment view; §7.35 answers the prior question "what is in the freezer NOW?".</li>
          <li><strong>§7.14 Vaccines</strong> consumes doses-on-hand to model rollout strategies; §7.35 supplies the doses-on-hand input by stockpile.</li>
          <li><strong>§7.25 Minister Pack</strong> defence-pack section pulls the pathogen-specific stockpile summary or gap callout; gaps belong in the Defence Pack as known-known limitations.</li>
          <li><strong>§7.4 Scenario</strong> + <strong>§7.5 Intervention Planner</strong> — if the scenario pathogen has NO stockpile, vaccine-based NPI options become unavailable in the basket; the gap callout makes that explicit.</li>
          <li><strong>§7.26 PHEIC Predictor</strong> — historical pattern: PHEIC declarations for stockpile-backed pathogens (yellow fever, polio) trigger faster international response than gap-pathogens (Lassa, Marburg).</li>
        </ol>
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Caveats</span></div>
        <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--bm-text-muted)', font: '12px var(--bm-font-mono)' }}>
          <li>Reference snapshot — not a live ICG / SNS / rescEU API. Live federated stockpile-status query is a future-phase wiring.</li>
          <li>Classified inventories (defence BSL-4 holdings) excluded by design.</li>
          <li>Quantities are point-in-time published figures; actual deployable totals require coordination-office query.</li>
          <li>Deployment latency dominated by airlift + cold-chain + last-mile, not stockpile-release approval — operator should add country-specific receiving-state friction.</li>
          <li>"Anthrax" + "botulism" + "polio" pathogen ids referenced in entries are non-catalog stub keys for this surface (pathogen-database does not currently carry those entries); the rows still render correctly because the gap-flag logic uses catalog membership not stockpile-membership.</li>
        </ul>
      </div>
    </SurfaceScaffold>
  );
}
